import * as React from 'react'
import { cn } from '@/lib/utils'

const CubeLoader = React.forwardRef(({ className, size = 'default', fullScreen = false, ...props }, ref) => {
    const fullScreenStyles = {}

    if (fullScreen) {
        fullScreenStyles.minHeight = '95vh'
        fullScreenStyles.maxHeight = '95vh'
    }

    const sizeClasses = {
        xs: 'tw:scale-[0.2]',
        sm: 'tw:scale-[0.3]',
        default: 'tw:scale-[0.4]',
        lg: 'tw:scale-[0.5]',
    }

    const cubes = [
        { id: 1, className: 'cube_count_1', zIndex: 2, left: '50px', top: '0px', delay: '1s' },
        { id: 2, className: 'cube_count_2', zIndex: 1, left: '-16px', top: '38px', delay: '1.1s' },
        { id: 3, className: 'cube_count_3', zIndex: 1, left: '116px', top: '38px', delay: '1.2s' },
        { id: 4, className: 'cube_count_4', zIndex: 2, left: '-16px', top: '116px', delay: '1.1s' },
        { id: 5, className: 'cube_count_5', zIndex: 1, left: '116px', top: '116px', delay: '1.2s' },
        { id: 6, className: 'cube_count_6', zIndex: 0, left: '50px', top: '154px', delay: '1.3s' },
    ]

    return (
        <div
            ref={ref}
            className={cn('tw:relative tw:flex tw:items-center tw:justify-center', className)}
            style={{
                '--anim-duration': '1.25s',
                '--cube-fill': '#0D2245',
                '--edge-color': '#2450BF',
                ...fullScreenStyles,
            }}
            {...props}
        >
            <div
                className={cn('scene', sizeClasses[size])}
                style={{
                    width: '200px',
                    height: '250px',
                    position: 'relative',
                }}
            >
                {cubes.map((cube) => (
                    <div
                        key={cube.id}
                        className={`cube ${cube.className}`}
                        style={{
                            width: '100px',
                            height: '100px',
                            position: 'absolute',
                            zIndex: cube.zIndex,
                            left: cube.left,
                            top: cube.top,
                            transform: 'rotateX(-36deg) rotateY(45deg)',
                            transformStyle: 'preserve-3d',
                            animation: `cube var(--anim-duration) ${cube.delay} ease infinite`,
                        }}
                    >
                        {['front', 'back', 'right', 'left', 'top', 'bottom'].map((face) => (
                            <div
                                key={face}
                                className={`cube__face cube__face--${face}`}
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    position: 'absolute',
                                    background: 'var(--cube-fill)',
                                    border: '6px solid var(--edge-color)',
                                    animation: `cube-face-${face} var(--anim-duration) ease infinite`,
                                    animationDelay: cube.delay,
                                    transform:
                                        face === 'front'
                                            ? 'rotateY(0deg) translateZ(50px)'
                                            : face === 'right'
                                              ? 'rotateY(90deg) translateZ(50px)'
                                              : face === 'back'
                                                ? 'rotateY(180deg) translateZ(50px)'
                                                : face === 'left'
                                                  ? 'rotateY(-90deg) translateZ(50px)'
                                                  : face === 'top'
                                                    ? 'rotateX(90deg) translateZ(50px)'
                                                    : 'rotateX(-90deg) translateZ(50px)',
                                    boxShadow:
                                        face === 'front'
                                            ? 'inset -6px -6px 0 0 var(--edge-color)'
                                            : face === 'left'
                                              ? 'inset 6px -6px 0 0 var(--edge-color)'
                                              : face === 'top'
                                                ? 'inset -6px 6px 0 0 var(--edge-color)'
                                                : 'none',
                                }}
                            />
                        ))}
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes cube {
                    0% {
                        transform: rotateX(-36deg) rotateY(45deg) rotateZ(0);
                    }
                    100% {
                        transform: rotateX(-126deg) rotateY(0deg) rotateZ(-45deg);
                    }
                }

                @keyframes cube-face-front {
                    0% {
                        box-shadow: inset -6px -6px 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                }

                @keyframes cube-face-right {
                    0% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                    50% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset 6px -6px 0 0 var(--edge-color);
                    }
                }

                @keyframes cube-face-back {
                    0% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset -6px -6px 0 0 var(--edge-color);
                    }
                }

                @keyframes cube-face-left {
                    0% {
                        box-shadow: inset 6px -6px 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                }

                @keyframes cube-face-top {
                    0% {
                        box-shadow: inset -6px 6px 0 0 var(--edge-color);
                    }
                    50% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset 6px -6px 0 0 var(--edge-color);
                    }
                }

                @keyframes cube-face-bottom {
                    0% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                    100% {
                        box-shadow: inset 0 0 0 0 var(--edge-color);
                    }
                }
            `}</style>
        </div>
    )
})

CubeLoader.displayName = 'CubeLoader'

export { CubeLoader }
